import { UNTITLED_CASE_TITLE } from "@cold-case/shared/manifest-display";

export type CaseRouletteOptions = {
  total: number;
  /** All playable seeds in the library (may exceed `total` for solo replay exclusion). */
  poolTotal?: number;
  titles: string[];
  chosenTitle: string;
};

export const ROULETTE_DISPLAY_COUNT = 5;
export const ROULETTE_SPIN_MS = 2800;
export const ROULETTE_FLASH_COUNT = 3;
export const ROULETTE_FLASH_MS = 165;
export const ROULETTE_COUNTDOWN_STEP_MS = 900;
export const ROULETTE_SPIN_TICK_MS_MIN = 48;
export const ROULETTE_SPIN_TICK_MS_MAX = 145;

export function rouletteCountdownLabels(): string[] {
  return ["5", "4", "3", "2", "1", "Go"];
}

/** Spin interval eases from fast to slow over [0,1] progress. */
export function rouletteSpinTickMs(progress: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return Math.round(
    ROULETTE_SPIN_TICK_MS_MIN +
      (ROULETTE_SPIN_TICK_MS_MAX - ROULETTE_SPIN_TICK_MS_MIN) * p * p
  );
}

/** Deduped spin titles; drops untitled placeholders when real titles exist. */
export function buildRouletteSpinPool(titles: string[], chosenTitle: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of titles) {
    const trimmed = t.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  const hasReal = out.some((t) => !/^untitled\s+case$/i.test(t));
  const filtered = hasReal ? out.filter((t) => !/^untitled\s+case$/i.test(t)) : out;
  if (filtered.length > 0) return filtered;
  const chosen = chosenTitle.trim();
  return chosen ? [chosen] : [UNTITLED_CASE_TITLE];
}

function shuffleCopy<T>(items: T[], rng: () => number): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

export type RouletteDisplayPlan = {
  list: string[];
  landIndex: number;
};

/** Five visible case titles; `chosenTitle` is always the landing row (default: last). */
export function buildRouletteDisplayList(
  titles: string[],
  chosenTitle: string,
  landIndex = ROULETTE_DISPLAY_COUNT - 1,
  rng: () => number = Math.random
): RouletteDisplayPlan {
  const pool = buildRouletteSpinPool(titles, chosenTitle);
  const chosen = chosenTitle.trim() || pool[0] || UNTITLED_CASE_TITLE;
  const idx = Math.min(ROULETTE_DISPLAY_COUNT - 1, Math.max(0, landIndex));

  const decoys: string[] = [];
  let bag = shuffleCopy(
    pool.filter((t) => t !== chosen),
    rng
  );
  if (bag.length === 0) bag = shuffleCopy(pool, rng);

  while (decoys.length < ROULETTE_DISPLAY_COUNT - 1) {
    if (bag.length === 0) {
      bag = shuffleCopy(pool.length > 0 ? pool : [chosen], rng);
    }
    decoys.push(bag.shift()!);
  }

  const list: string[] = [];
  let d = 0;
  for (let i = 0; i < ROULETTE_DISPLAY_COUNT; i++) {
    if (i === idx) list.push(chosen);
    else list.push(decoys[d++]!);
  }
  return { list, landIndex: idx };
}

/** Highlight indices for the list roulette (ends on `landIndex`). */
export function planRouletteSpinSteps(
  landIndex: number,
  listLength = ROULETTE_DISPLAY_COUNT,
  minCycles = 2
): number[] {
  const len = Math.max(1, listLength);
  const land = ((landIndex % len) + len) % len;
  const minSteps = minCycles * len + land + 1;
  const steps: number[] = [];
  let cursor = 0;
  while (steps.length < minSteps) {
    steps.push(cursor);
    cursor = (cursor + 1) % len;
  }
  while (steps[steps.length - 1] !== land) {
    steps.push((steps[steps.length - 1]! + 1) % len);
  }
  return steps;
}

/** Per-step delays scaled to `totalMs`, easing from fast to slow. */
export function spinStepDelaysMs(stepCount: number, totalMs = ROULETTE_SPIN_MS): number[] {
  if (stepCount <= 0) return [];
  const raw = Array.from({ length: stepCount }, (_, i) => {
    const progress = stepCount <= 1 ? 1 : i / (stepCount - 1);
    return rouletteSpinTickMs(progress);
  });
  const sum = raw.reduce((a, b) => a + b, 0);
  const scale = sum > 0 ? totalMs / sum : 1;
  return raw.map((d) => Math.max(40, Math.round(d * scale)));
}

/** Smaller type on narrow screens when titles would wrap. */
export function rouletteTitleSizeClass(title: string): string {
  const len = title.trim().length;
  if (len > 52) return "case-roulette-row--long";
  if (len > 36) return "case-roulette-row--medium";
  return "";
}

/** @deprecated Used by legacy tests; display list spin uses `planRouletteSpinSteps`. */
export function pickRouletteSpinTitle(
  titles: string[],
  chosenTitle: string,
  rng: number
): string {
  if (titles.length === 0) return chosenTitle;
  const idx = Math.floor(rng * titles.length) % titles.length;
  return titles[idx] ?? chosenTitle;
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

let activeTimers: ReturnType<typeof setTimeout>[] = [];
let runGeneration = 0;

function delay(ms: number, gen: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      activeTimers = activeTimers.filter((id) => id !== t);
      if (gen !== runGeneration) {
        reject(new RouletteAbortedError());
        return;
      }
      resolve();
    }, ms);
    activeTimers.push(t);
  });
}

function clearActiveTimers(): void {
  for (const t of activeTimers) clearTimeout(t);
  activeTimers = [];
}

export class RouletteAbortedError extends Error {
  constructor() {
    super("case roulette aborted");
    this.name = "RouletteAbortedError";
  }
}

function setAvailableCount(el: HTMLElement, total: number, poolTotal?: number): void {
  el.textContent = "";
  const pool = poolTotal ?? total;
  if (pool > total) {
    el.append("Cases left for you: ");
    const strong = document.createElement("strong");
    strong.className = "case-roulette-count";
    strong.textContent = String(total);
    el.append(strong);
    const sub = document.createElement("div");
    sub.className = "case-roulette-pool-hint";
    sub.textContent = `${pool} in the case library`;
    el.append(sub);
    return;
  }
  el.append("Available cases: ");
  const strong = document.createElement("strong");
  strong.className = "case-roulette-count";
  strong.textContent = String(total);
  el.append(strong);
}

function resetRouletteClasses(
  overlay: HTMLElement,
  listEl: HTMLElement,
  countdownEl: HTMLElement
): void {
  overlay.classList.remove("open");
  listEl.classList.remove("case-roulette-list--spinning");
  countdownEl.classList.remove(
    "case-roulette-countdown-tick",
    "case-roulette-countdown-go"
  );
}

export function closeCaseRoulette(): void {
  runGeneration += 1;
  clearActiveTimers();
  const overlay = document.getElementById("case-roulette-overlay");
  if (!overlay) return;
  overlay.setAttribute("aria-hidden", "true");
  const listEl = document.getElementById("case-roulette-list");
  const countdownEl = document.getElementById("case-roulette-countdown");
  const phaseEl = document.getElementById("case-roulette-phase");
  if (listEl && countdownEl) {
    resetRouletteClasses(overlay, listEl, countdownEl);
  } else {
    overlay.classList.remove("open");
  }
  if (listEl) listEl.replaceChildren();
  if (countdownEl) countdownEl.textContent = "";
  if (phaseEl) phaseEl.textContent = "";
}

function ensureOverlayElements(): {
  overlay: HTMLElement;
  availableEl: HTMLElement;
  phaseEl: HTMLElement;
  listEl: HTMLElement;
  countdownEl: HTMLElement;
} | null {
  const overlay = document.getElementById("case-roulette-overlay");
  const availableEl = document.getElementById("case-roulette-available");
  const phaseEl = document.getElementById("case-roulette-phase");
  const listEl = document.getElementById("case-roulette-list");
  const countdownEl = document.getElementById("case-roulette-countdown");
  if (!overlay || !availableEl || !phaseEl || !listEl || !countdownEl) return null;
  return {
    overlay,
    availableEl,
    phaseEl,
    listEl,
    countdownEl,
  };
}

function pulseCountdown(countdownEl: HTMLElement, isGo: boolean): void {
  countdownEl.classList.remove("case-roulette-countdown-tick", "case-roulette-countdown-go");
  void countdownEl.offsetWidth;
  countdownEl.classList.add(
    isGo ? "case-roulette-countdown-go" : "case-roulette-countdown-tick"
  );
}

function renderRouletteList(
  listEl: HTMLElement,
  titles: string[],
  activeIndex: number | null,
  winnerIndex: number | null,
  flashing: boolean
): void {
  listEl.replaceChildren();
  titles.forEach((title, i) => {
    const li = document.createElement("li");
    li.className = "case-roulette-row";
    li.setAttribute("role", "option");
    li.setAttribute("aria-selected", activeIndex === i || winnerIndex === i ? "true" : "false");
    const sizeClass = rouletteTitleSizeClass(title);
    if (sizeClass) li.classList.add(sizeClass);
    if (activeIndex === i) li.classList.add("case-roulette-row--active");
    if (winnerIndex === i) {
      li.classList.add("case-roulette-row--winner");
      if (flashing) li.classList.add("case-roulette-row--flash");
    }
    li.textContent = title;
    listEl.appendChild(li);
  });
}

async function flashWinner(
  listEl: HTMLElement,
  titles: string[],
  landIndex: number,
  gen: number,
  reducedMotion: boolean
): Promise<void> {
  const flashes = reducedMotion ? 1 : ROULETTE_FLASH_COUNT;
  for (let f = 0; f < flashes; f++) {
    renderRouletteList(listEl, titles, null, landIndex, true);
    await delay(reducedMotion ? 120 : ROULETTE_FLASH_MS, gen);
    renderRouletteList(listEl, titles, null, landIndex, false);
    if (f < flashes - 1) {
      await delay(reducedMotion ? 80 : ROULETTE_FLASH_MS * 0.55, gen);
    }
  }
  renderRouletteList(listEl, titles, null, landIndex, false);
}

export async function runCaseRoulette(opts: CaseRouletteOptions): Promise<void> {
  const dom = ensureOverlayElements();
  if (!dom) return;

  const gen = ++runGeneration;
  clearActiveTimers();

  const { overlay, availableEl, phaseEl, listEl, countdownEl } = dom;
  const { list, landIndex } = buildRouletteDisplayList(opts.titles, opts.chosenTitle);
  const reducedMotion = prefersReducedMotion();

  setAvailableCount(availableEl, opts.total, opts.poolTotal);
  resetRouletteClasses(overlay, listEl, countdownEl);
  phaseEl.textContent = reducedMotion ? "Your assignment" : "Drawing from the case files…";
  countdownEl.textContent = "";
  renderRouletteList(listEl, list, null, null, false);
  overlay.classList.add("open");
  overlay.setAttribute("aria-hidden", "false");

  try {
    if (!reducedMotion) {
      listEl.classList.add("case-roulette-list--spinning");
      const steps = planRouletteSpinSteps(landIndex, list.length);
      const delays = spinStepDelaysMs(steps.length);
      for (let i = 0; i < steps.length; i++) {
        if (gen !== runGeneration) throw new RouletteAbortedError();
        renderRouletteList(listEl, list, steps[i]!, null, false);
        await delay(delays[i] ?? ROULETTE_SPIN_TICK_MS_MAX, gen);
      }
      listEl.classList.remove("case-roulette-list--spinning");
    }

    phaseEl.textContent = "Your case";
    renderRouletteList(listEl, list, null, landIndex, false);
    await flashWinner(listEl, list, landIndex, gen, reducedMotion);

    phaseEl.textContent = "Starting investigation";
    const labels = rouletteCountdownLabels();
    const stepMs = reducedMotion ? 550 : ROULETTE_COUNTDOWN_STEP_MS;
    for (const label of labels) {
      if (gen !== runGeneration) throw new RouletteAbortedError();
      const isGo = label === "Go";
      countdownEl.textContent = isGo ? label : `${label}…`;
      if (!reducedMotion) pulseCountdown(countdownEl, isGo);
      await delay(isGo ? 450 : stepMs, gen);
    }
  } catch (err) {
    if (err instanceof RouletteAbortedError) return;
    throw err;
  } finally {
    if (gen === runGeneration) {
      closeCaseRoulette();
    }
  }
}
